import objectPath from 'object-path';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { config } from 'config/env';
import Sidebar from '../../../app/components/core/layout/Sidebar/Sidebar';
import {
  SIDEBAR_MOBILE_BREAKPOINT,
  SIDEBAR_WIDTH,
} from '../../../app/components/core/layout/Sidebar/styles';
import ImpersonateBanner from '../../../app/components/ImpersonateBanner';
import { ModalErrorPayment } from '../../../app/components/ModalErrorPayment';
import ModalOffer from '../../../app/components/ModalOffer';
import ModalRequestAddress from '../../../app/components/ModalRequestAddress';
import ModalSubscriptionReached from '../../../app/components/ModalSubscriptionReached';
import { FEATURE_FLAGS } from '../../../app/constants/FeatureFlag';
import { ROUTES_PATH } from '../../../app/constants/Routes';
import { SUBSCRIPTION_STATUS } from '../../../app/constants/subscriptionStatus';
import useFeatureFlag from '../../../app/hooks/useFeatureFlag';
import { useIsFreemiumOnboardingUser } from '../../../app/hooks/useIsFreemiumOnboardingUser';
import * as auth from '../../../app/modules/Auth/_redux/authRedux';
import { checkPathnameIncludes } from '../../_helpers/Pages';
import { useHtmlClassService } from '../_core/MetronicLayout';
import ConfettiShower from './ConfettiShower';
import { Footer } from './footer/Footer';
import GamificationListener from '../../../app/components/GamificationListener';
import TaskCompletionListener from '../../../app/components/TaskCompletionListener';
import { useSidebarMobile } from '../../../app/components/core/layout/Sidebar/hooks';
import { SidebarLayoutProvider } from '../../../app/components/core/layout/SidebarLayoutProvider';
import { useHeaderNavigationContext } from '../../../app/contexts/HeaderNavigationContext';
import HeaderNavigation from '../../../app/components/HeaderNavigation';
import { AnimateLoading } from '../../_partials/controls';

const PAGES_WITHOUT_LAYOUT_INITIALIZATION = [
  '/dashboard',
  '/jusfinder',
  '/processual_query',
  '/jusfypay',
  '/gpt/business',
  '/relatorio/editar',
  '/clientes',
  '/gpt/config',
  '/meu-drive',
  ROUTES_PATH.LAWSUIT_MONITOR.BASE,
  ROUTES_PATH.LAWSUIT_MONITOR.BASE_V2,
  ROUTES_PATH.MAIN.JURISPRUDENCIA,
  ROUTES_PATH.MAIN.ACADEMY,
  ROUTES_PATH.CONTABILIDADE.LANDING_PAGE,
  ROUTES_PATH.JUSZAP.LANDING_PAGE,
];

export function Layout({ children }) {
  const { config: headerNavigationConfig } = useHeaderNavigationContext();
  const { isFeatureFlagEnable: enableRequestAddress } = useFeatureFlag(
    FEATURE_FLAGS.KILL_SWITCH.ENABLE_REQUEST_ADDRESS,
  );
  const {
    isFreemium: isFreemiumOnboardingUser,
    isResolved: isFreemiumOnboardingUserResolved,
  } = useIsFreemiumOnboardingUser();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const subscription_error = useSelector(
    (state) => state.subscription.subscription_error,
  );
  const subscription = useSelector(
    (state) => state.subscription.subscription_status.info,
  );
  const [modalRequest, setModalRequest] = useState(false);

  const [asideWidth, setAsideWidth] = useState(SIDEBAR_WIDTH);
  const [isModalErrorOpen, setIsModalErrorOpen] = useState(false);
  const isSidebarMobile = useSidebarMobile(SIDEBAR_MOBILE_BREAKPOINT);

  const steps = useSelector((state) => state?.app?.first_steps);

  useEffect(() => {
    // Usuários freemium (onboarding novo) capturam endereço na etapa
    // dedicada (`/onboarding/endereco`), não neste modal legado.
    if (
      user?.should_fill_data &&
      enableRequestAddress &&
      isFreemiumOnboardingUserResolved &&
      !isFreemiumOnboardingUser
    ) {
      const closedAt = localStorage.getItem('modal_request_address_closed_at');
      if (closedAt) {
        const now = new Date().getTime();
        const twentyFourHours = 24 * 60 * 60 * 1000;
        const timeDiff = now - parseInt(closedAt, 10);

        if (timeDiff < twentyFourHours) {
          setModalRequest(false);
          return;
        }
      }

      setModalRequest(true);
    } else {
      setModalRequest(false);
    }
  }, [
    user?.should_fill_data,
    enableRequestAddress,
    isFreemiumOnboardingUser,
    isFreemiumOnboardingUserResolved,
  ]);

  useEffect(() => {
    dispatch({
      type: 'LOAD_CREDITS_RESUME',
      payload: { clientId: user.id },
    });

    dispatch({ type: 'LOAD_UNIVERSAL_JUSFINDER_STATUS' });

    dispatch({
      type: 'LOAD_SUBSCRIPTION_STATUS',
      payload: {
        callback: (provider) => {
          dispatch(auth.actions.updateUserProvider(provider));
        },
      },
    });
  }, []);

  useEffect(() => {
    if (subscription_error) {
      setIsModalErrorOpen(true);
    } else {
      setIsModalErrorOpen(false);
    }
  }, [subscription_error]);

  useEffect(() => {
    if (
      [
        SUBSCRIPTION_STATUS.UNPAID,
        SUBSCRIPTION_STATUS.WAITING_PAYMENT,
        SUBSCRIPTION_STATUS.PENDING_PAYMENT,
      ].includes(subscription?.status)
    ) {
      dispatch({
        type: 'FORCE_LOAD_SUBSCRIPTION_STATUS',
        payload: {
          values: {
            subscription_id: subscription?.subscription_id,
          },
        },
      });
    }
  }, [subscription?.status]);

  const closeErrorModal = () => {
    setIsModalErrorOpen(false);
    dispatch({ type: 'CLEAR_SUBSCRIPTION_ERROR' });
  };

  const location = useLocation();

  const hasProfileSurveyOrigin = location?.state?.origin === '/profile/survey';

  const uiService = useHtmlClassService();

  const layoutProps = useMemo(() => {
    const contentContainerClasses = uiService
      .getClasses('content_container', true)
      .split(' ')
      .filter((cls) => cls !== 'mt-50')
      .join(' ');

    return {
      selfLayout: objectPath.get(uiService.config, 'self.layout'),
      contentCssClasses: uiService.getClasses('content', true),
      contentContainerClasses,
      contentExtended: objectPath.get(uiService.config, 'content.extended'),
    };
  }, [uiService]);

  useEffect(() => {
    if (layoutProps.selfLayout === 'blank') {
      return;
    }

    document.body.classList.add('new-sidebar-enabled');
    ['data-offcanvas-aside', 'data-offcanvas-aside-offcanvas-default'].forEach(
      (attr) => {
        document.body.removeAttribute(attr);
      },
    );

    return () => document.body.classList.remove('new-sidebar-enabled');
  }, [layoutProps.selfLayout]);

  const wrapperClassName = [
    'd-flex flex-column flex-row-fluid wrapper',
    'new-sidebar',
    isSidebarMobile && 'kt_wrapper--mobile',
  ]
    .filter(Boolean)
    .join(' ');

  const wrapperStyle = {
    marginLeft: isSidebarMobile ? undefined : `${asideWidth}px`,
    paddingLeft: 0,
  };

  if (layoutProps.selfLayout === 'blank') {
    return <div className="d-flex flex-column flex-root">{children}</div>;
  }

  return (
    <>
      {hasProfileSurveyOrigin && steps?.USER_PROFILE ? (
        <ConfettiShower />
      ) : null}

      <ModalOffer />
      <ModalSubscriptionReached />
      <GamificationListener />
      <TaskCompletionListener />
      <ModalErrorPayment
        isOpen={isModalErrorOpen}
        onClose={closeErrorModal}
        onSupportClick={() =>
          window.open(config.support.whatsappUrl, '_blank', 'noopener,noreferrer')
        }
      />
      <ModalRequestAddress
        isOpen={modalRequest}
        onClose={() => setModalRequest(false)}
      />
      <div className="d-flex flex-column flex-root">
        <div className="d-flex flex-row flex-column-fluid page">
          <SidebarLayoutProvider
            asideWidth={asideWidth}
            isMobile={isSidebarMobile}
          >
            <Sidebar onWidthChange={setAsideWidth} />

            <div
              className={wrapperClassName}
              id="kt_wrapper"
              style={wrapperStyle}
            >
              <AnimateLoading />
              {headerNavigationConfig && (
                <HeaderNavigation {...headerNavigationConfig} />
              )}

              {!checkPathnameIncludes(PAGES_WITHOUT_LAYOUT_INITIALIZATION) ? (
                <div
                  id="kt_content"
                  className={`content ${layoutProps.contentCssClasses} d-flex flex-column flex-column-fluid`}
                >
                  {!layoutProps.contentExtended && (
                    <div className="d-flex flex-column-fluid">
                      <div className={layoutProps.contentContainerClasses}>
                        {children}
                      </div>
                    </div>
                  )}

                  {layoutProps.contentExtended && children}
                </div>
              ) : (
                <>{children}</>
              )}

              <Footer />
            </div>
          </SidebarLayoutProvider>
        </div>
      </div>

      <ImpersonateBanner />
    </>
  );
}
